'use client';
import { createContext, useContext, useState, useMemo, useEffect, useCallback, useRef } from "react";
import { AuthContext } from "./Authentication";
import { AxiosContext } from "./Axios";
import { getUserDetails } from "@/services/userLoginApi";

export const SessionContext = createContext();

function fetchUserDetails(axios, data, setUser) {
  if (data.isAuthenticated && axios) {
    return axios
      .post('/getUserDetails', {
        userID: data.userId.toString(),
        session_token: data.token,
      })
      .then((res) => {
        if (data.isAuthenticated) {
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'user_details_updated',
            user: {
              id: data.userId.toString(),
              email: res.data.email,
              name:
                res.data.firstname +
                (res.data.lastname ? ` ${res.data.lastname}` : ''),
              phone: res.data.phone,
            },
          });
        }
        setUser(res.data);
        return res.data;
      })
      .catch((err) => {
        if (err.response?.status === 401) {
          // Token might be invalid, clear auth
          localStorage.removeItem('token');
          localStorage.removeItem('userId');
        }
        throw err;
      });
  } else {
    setUser(null);
    return Promise.reject(new Error('Not authenticated'));
  }
}

export default function Session({ children }) {
  const auth = useContext(AuthContext);
  const axios = useContext(AxiosContext);
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(false);
  const fetchVersionRef = useRef(0);
  const loadedUserIdRef = useRef(null);
  const isDemo = useMemo(() => user && user.demo, [user]);

  const forceRefresh = useCallback(() => {
    if (auth.isAuthenticated && axios && auth.userId) {
      const requestUserId = String(auth.userId);
      const fetchVersion = fetchVersionRef.current + 1;
      fetchVersionRef.current = fetchVersion;
      setUserLoading(true);
      
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'login',
        user: {
          id: requestUserId,
        },
      });

      const loading = getUserDetails();

      const existingCartId = localStorage.getItem('cartId');
      loading
        .then((userData) => {
          if (fetchVersionRef.current !== fetchVersion) return false;
          setUser(userData);
          loadedUserIdRef.current = requestUserId;
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'user_details_updated',
            user: {
              id: requestUserId,
              email: userData.email,
              name:
                (userData.firstname || '') +
                (userData.lastname ? ` ${userData.lastname}` : ''),
              phone: userData.phone,
            },
          });
          if (existingCartId) {
            const data = {
              cartID: existingCartId,
              userID: requestUserId,
            };
            if (auth.token) {
              data.session_token = auth.token;
            }
            return axios.post('/validateCart', data);
          } else {
            return Promise.resolve(false);
          }
        })
        .then(() => {})
        .catch((err) => {
          if (fetchVersionRef.current !== fetchVersion) return;
          if (err?.status === 401 || err?.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            setUser(null);
            loadedUserIdRef.current = null;
          }
        })
        .finally(() => {
          if (fetchVersionRef.current === fetchVersion) {
            setUserLoading(false);
          }
        });
    } else if (!auth.isAuthenticated) {
      setUser(null);
      loadedUserIdRef.current = null;
      setUserLoading(false);
    }
  }, [auth.isAuthenticated, auth.userId, auth.token, axios]);

  useEffect(() => {
    if (auth.isLoading) return;

    if (!auth.isAuthenticated) {
      setUser(null);
      loadedUserIdRef.current = null;
      fetchVersionRef.current += 1;
      setUserLoading(false);
      return;
    }

    const activeUserId = auth.userId != null ? String(auth.userId) : null;
    if (!activeUserId) return;

    if (loadedUserIdRef.current !== activeUserId) {
      setUser(null);
      forceRefresh();
    }
  }, [auth.isAuthenticated, auth.isLoading, auth.userId, forceRefresh]);

  const value = useMemo(
    () => ({ user, isDemo, forceRefresh, userLoading }),
    [user, isDemo, forceRefresh, userLoading]
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
    </SessionContext.Provider>
  );
}